import * as THREE from 'three';
import type { Pose, Motion } from '../core/ActionObject';
import { ObjectA3 } from '../core/ObjectA3';
import { Quat, Vec3 } from '../core/LinearMath';
import type { PhysicsWorld } from '../core/Physics';

/**
 * Three.js の `THREE.AnimationClip` を `Motion` として再生するクラスです。
 * glTF や BVH から読み込まれたアニメーションデータの再生に使用されます。
 *
 * 通常は `GLTF` や `Acerola3D` が内部で自動的に生成するため、直接インスタンス化する機会は少ないです。
 */
export class ClipMotion implements Motion {
  /** このモーションの名前。 */
  name: string;
  /** 現在の再生位置（秒）。 */
  time: number;
  /** 再生完了時のリスナー。 */
  finishListener?: ()=>void;
  /** このモーションの総再生時間（秒）。 */
  duration: number;
  /** 一時停止中かどうか。 */
  isPaused: boolean;
  /** 再生回数。 */
  playCount: number;
  /** ボーンごとの補間器。内部処理用です。 */
  interpolants: Record<string,THREE.Interpolant>;

  constructor(clip: THREE.AnimationClip, name?: string) {
    this.name = name?name:clip.name;
    this.time = 0;
    this.duration = clip.duration;
    this.isPaused = false;
    this.playCount = 0;
    this.interpolants = {};
    for (const track of clip.tracks) {
      const valueSize = track.getValueSize();
      let interpolant;
      if (track.getInterpolation()===THREE.InterpolateDiscrete) {
        interpolant = new THREE.DiscreteInterpolant(
          track.times,
          track.values,
          valueSize
        );
      // } else if (track.getInterpolation()===THREE.InterpolateSmooth) { // 意味不明
      // } else if (track.getInterpolation()===THREE.InterpolateBezier) { // たぶんありえない
      // } else if (track.getInterpolation()===THREE.InterpolateLinear) { // デフォルト扱いにする
      } else { // デフォルトはTHREE.InterpolateLinearということで
        if (track.ValueTypeName === 'quaternion') {
          interpolant = new THREE.QuaternionLinearInterpolant(
            track.times,
            track.values,
            valueSize
          );
        } else {
          interpolant = new THREE.LinearInterpolant(
            track.times,
            track.values,
            valueSize
          );
        }
      }
      this.interpolants[track.name] = interpolant;
    }
  }

  init(_objectA3: ObjectA3) {
    // nothing to do
  }

  addOneselfToPhysics(_world: PhysicsWorld) {}
  removeOneselfFromPhysics(_world: PhysicsWorld) {}

  setPause(p: boolean) {
    this.isPaused = p;
  }

  setTime(time: number) {
    this.time = time;
  }

  setFinishListener(listener: ()=>void|undefined) {
    this.finishListener=listener
  }

  update(dt: number): Pose {
    this.time += dt;
    if (this.time > this.duration) {
      this.time -= this.duration;
      this.playCount++;
      if (this.finishListener)
        this.finishListener();
    }
    const pose: Pose = {};
    for (const [name,interpolant] of Object.entries(this.interpolants)) {
      const nodeName = name.substring(0,name.lastIndexOf('.'));
      const property = name.substring(name.lastIndexOf('.')+1,name.length);
      let data = pose[nodeName];
      if (!data) {
        data = {};
        pose[nodeName] = data;
      }
      const res = interpolant.evaluate(this.time);
      if (property === 'position') {
        data.loc = new Vec3(res[0],res[1],res[2]);
      } else if (property === 'quaternion') {
        data.quat = new Quat(res[0],res[1],res[2],res[3]);
      } else if (property === 'scale') {
        data.scale = new Vec3(res[0],res[1],res[2]);
      } else if (property === 'morphTargetInfluences') {
        if (!data.morphs) data.morphs = [];
        // いいのか？
        const vals:number[] = Array.from(res);
        data.morphs.push({name: nodeName,vals});
      } else {
        console.warn(`ClipMotion: update. unknown property(${property})`);
      }
    }
    return pose;
  }
}
